// CameraControls - Camera preset buttons panel
import React, { useState, RefObject } from 'react';
import * as THREE from 'three';
import { ThemeType } from '../../types/visualization';
import { THEMES } from '../../constants/visualizationThemes';

interface CameraControlsProps {
  controlsRef: RefObject<any>;
  theme: ThemeType;
}

export default function CameraControls({ controlsRef, theme }: CameraControlsProps) {
  const [collapsed, setCollapsed] = useState(true);
  const t = THEMES[theme];

  const buttonStyle: React.CSSProperties = {
    padding: '5px 6px',
    background: t.buttonBg,
    border: `1px solid ${t.buttonBorder}`,
    borderRadius: '3px',
    color: t.textMuted,
    fontSize: '8px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.1s ease',
    textAlign: 'center',
  };

  const setCameraView = (position: [number, number, number], target: [number, number, number] = [0, 0, 0]) => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const camera = controls.object;
    camera.position.set(...position);
    controls.target.set(...target);
    controls.update();
  };

  const views = {
    front: { pos: [0, 5, 40] as [number, number, number], label: 'Front' },
    back: { pos: [0, 5, -40] as [number, number, number], label: 'Back' },
    top: { pos: [0, 50, 0.1] as [number, number, number], label: 'Top' },
    right: { pos: [40, 5, 0] as [number, number, number], label: 'Right' },
    left: { pos: [-40, 5, 0] as [number, number, number], label: 'Left' },
    isoFrontRight: { pos: [30, 25, 30] as [number, number, number], label: 'Iso FR' },
    isoFrontLeft: { pos: [-30, 25, 30] as [number, number, number], label: 'Iso FL' },
    isoBackRight: { pos: [30, 25, -30] as [number, number, number], label: 'Iso BR' },
    isoBackLeft: { pos: [-30, 25, -30] as [number, number, number], label: 'Iso BL' },
  };

  const zoomToFit = () => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const camera = controls.object;
    camera.position.set(0, 20, 50);
    controls.target.set(0, 0, 0);
    controls.update();
  };

  const zoomIn = () => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const camera = controls.object;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    camera.position.addScaledVector(direction, 5);
    controls.update();
  };

  const zoomOut = () => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const camera = controls.object;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    camera.position.addScaledVector(direction, -5);
    controls.update();
  };

  return (
    <div style={{
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: t.panelBg,
      border: `1px solid ${t.panelBorder}`,
      borderRadius: '6px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      transition: 'all 0.2s ease',
      zIndex: 100,
    }}>
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '6px 12px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${t.panelBorder}`,
          userSelect: 'none',
          gap: '6px',
        }}
      >
        <span style={{ fontSize: '9px', fontWeight: 500, color: t.textMuted, letterSpacing: '0.5px' }}>
          CAMERA
        </span>
        <span style={{ fontSize: '8px', color: t.textMuted, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
          ▶
        </span>
      </div>

      {!collapsed && (
        <div style={{ padding: '8px' }}>
          <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
            <button onClick={zoomIn} style={{ ...buttonStyle, flex: 1 }}>+</button>
            <button onClick={zoomToFit} style={{ ...buttonStyle, flex: 1 }}>Fit</button>
            <button onClick={zoomOut} style={{ ...buttonStyle, flex: 1 }}>−</button>
          </div>

          <div style={{ fontSize: '8px', color: t.textDimmed, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Views
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '2px', marginBottom: '6px' }}>
            <button onClick={() => setCameraView(views.front.pos)} style={buttonStyle}>F</button>
            <button onClick={() => setCameraView(views.back.pos)} style={buttonStyle}>B</button>
            <button onClick={() => setCameraView(views.top.pos)} style={buttonStyle}>T</button>
            <button onClick={() => setCameraView(views.left.pos)} style={buttonStyle}>L</button>
            <button onClick={() => setCameraView(views.right.pos)} style={buttonStyle}>R</button>
          </div>

          <div style={{ fontSize: '8px', color: t.textDimmed, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Iso
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px' }}>
            <button onClick={() => setCameraView(views.isoFrontLeft.pos)} style={buttonStyle}>FL</button>
            <button onClick={() => setCameraView(views.isoFrontRight.pos)} style={buttonStyle}>FR</button>
            <button onClick={() => setCameraView(views.isoBackLeft.pos)} style={buttonStyle}>BL</button>
            <button onClick={() => setCameraView(views.isoBackRight.pos)} style={buttonStyle}>BR</button>
          </div>
        </div>
      )}
    </div>
  );
}
